import { useState } from 'react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { Modal } from '@/components/ui/Modal'
import { Input } from '@/components/ui/Input'
import { formatDate } from '@/lib/utils'
import {
  Check,
  Circle,
  Plus,
  Clock,
  Flag,
  AlertCircle,
  Trash2,
} from 'lucide-react'
import type { ProjectMilestone, ProjectStatus, MilestoneFormData } from '@/types/project'
import { PROJECT_STATUS_CONFIG } from '@/types/project'
import { useCreateMilestone, useCompleteMilestone, useDeleteMilestone } from '@/hooks/useProjects'

interface ProgressTrackerProps {
  projectId: string
  status: ProjectStatus
  progression: number
  milestones?: ProjectMilestone[]
  onProgressionChange?: (value: number) => void
  readOnly?: boolean
}

function ProgressBar({
  value,
  showLabel = true,
  size = 'md',
}: {
  value: number
  showLabel?: boolean
  size?: 'sm' | 'md' | 'lg'
}) {
  const heights = {
    sm: 'h-2',
    md: 'h-3',
    lg: 'h-4',
  }

  return (
    <div className="flex items-center gap-3">
      <div className={`flex-1 bg-beige-200 rounded-full ${heights[size]}`}>
        <div
          className={`bg-gradient-to-r from-mint-400 to-mint-600 ${heights[size]} rounded-full transition-all duration-500 ease-out`}
          style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
        />
      </div>
      {showLabel && (
        <span className="text-sm font-semibold text-brown-700 min-w-[3rem] text-right">
          {value}%
        </span>
      )}
    </div>
  )
}

function MilestoneItem({
  milestone,
  onComplete,
  onDelete,
  readOnly,
}: {
  milestone: ProjectMilestone
  onComplete: () => void
  onDelete: () => void
  readOnly?: boolean
}) {
  const isCompleted = milestone.status === 'completed'
  const isPast = new Date(milestone.date_prevue) < new Date() && !isCompleted
  const isToday =
    new Date(milestone.date_prevue).toDateString() === new Date().toDateString()

  return (
    <div
      className={`relative flex items-start gap-4 py-4 ${
        isCompleted ? 'opacity-75' : ''
      }`}
    >
      {/* Timeline connector */}
      <div className="absolute left-5 top-12 bottom-0 w-0.5 bg-beige-200" />

      {/* Status icon */}
      <div
        className={`relative z-10 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
          isCompleted
            ? 'bg-mint-500 text-white'
            : isPast
            ? 'bg-red-100 text-red-600 border-2 border-red-300'
            : 'bg-beige-100 text-brown-400 border-2 border-beige-300'
        }`}
      >
        {isCompleted ? (
          <Check className="w-5 h-5" />
        ) : isPast ? (
          <AlertCircle className="w-5 h-5" />
        ) : (
          <Circle className="w-5 h-5" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-1">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h4
              className={`font-medium ${
                isCompleted
                  ? 'text-brown-500 line-through'
                  : 'text-brown-900'
              }`}
            >
              {milestone.titre}
            </h4>
            {milestone.description && (
              <p className="text-sm text-brown-500 mt-0.5">
                {milestone.description}
              </p>
            )}
            <div className="flex items-center gap-2 mt-2">
              <Clock className="w-3.5 h-3.5 text-brown-400" />
              <span
                className={`text-xs ${
                  isPast ? 'text-red-600 font-medium' : 'text-brown-500'
                }`}
              >
                {isCompleted
                  ? `Termine le ${formatDate(milestone.date_completee!, {
                      day: 'numeric',
                      month: 'short',
                    })}`
                  : isToday
                  ? "Aujourd'hui"
                  : isPast
                  ? `En retard de ${Math.ceil(
                      (new Date().getTime() -
                        new Date(milestone.date_prevue).getTime()) /
                        (1000 * 60 * 60 * 24)
                    )} jours`
                  : formatDate(milestone.date_prevue, {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
              </span>
            </div>
          </div>

          {!readOnly && (
            <div className="flex items-center gap-1">
              {!isCompleted && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onComplete}
                  title="Marquer comme termine"
                >
                  <Check className="w-4 h-4 text-mint-600" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onDelete}
                title="Supprimer"
              >
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function AddMilestoneModal({
  isOpen,
  onClose,
  onAdd,
}: {
  isOpen: boolean
  onClose: () => void
  onAdd: (data: MilestoneFormData) => void
}) {
  const [titre, setTitre] = useState('')
  const [description, setDescription] = useState('')
  const [datePrevue, setDatePrevue] = useState('')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!titre.trim() || !datePrevue) return

    onAdd({
      titre: titre.trim(),
      description: description.trim() || '',
      date_prevue: datePrevue,
    })

    setTitre('')
    setDescription('')
    setDatePrevue('')
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ajouter un jalon">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Titre"
          value={titre}
          onChange={(e) => setTitre(e.target.value)}
          placeholder="Ex: Livraison maquettes"
          required
        />
        <Input
          label="Description (optionnel)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Details sur ce jalon..."
        />
        <Input
          type="date"
          label="Date prevue"
          value={datePrevue}
          onChange={(e) => setDatePrevue(e.target.value)}
          required
        />
        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" disabled={!titre.trim() || !datePrevue}>
            Ajouter
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export function ProgressTracker({
  projectId,
  status,
  progression,
  milestones = [],
  onProgressionChange: _onProgressionChange,
  readOnly = false,
}: ProgressTrackerProps) {
  const [showAddModal, setShowAddModal] = useState(false)
  const createMilestone = useCreateMilestone()
  const completeMilestone = useCompleteMilestone()
  const deleteMilestone = useDeleteMilestone()

  const statusConfig = PROJECT_STATUS_CONFIG[status]

  const completedMilestones = milestones.filter((m) => m.status === 'completed')
  const pendingMilestones = milestones.filter((m) => m.status === 'pending')

  const sortedMilestones = [...milestones].sort((a, b) => a.ordre - b.ordre)

  const handleAddMilestone = async (data: MilestoneFormData) => {
    await createMilestone.mutateAsync({ projectId, data })
  }

  const handleCompleteMilestone = async (milestoneId: string) => {
    await completeMilestone.mutateAsync({ milestoneId, projectId })
  }

  const handleDeleteMilestone = async (milestoneId: string) => {
    if (confirm('Voulez-vous vraiment supprimer ce jalon ?')) {
      await deleteMilestone.mutateAsync({ milestoneId, projectId })
    }
  }

  return (
    <Card>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-mint-100 flex items-center justify-center">
              <Flag className="w-5 h-5 text-mint-600" />
            </div>
            <div>
              <h3 className="font-semibold text-brown-900">Suivi d'avancement</h3>
              <p className="text-sm text-brown-500">
                {completedMilestones.length} / {milestones.length} jalons completes
              </p>
            </div>
          </div>
          <Badge variant={statusConfig.color}>{statusConfig.label}</Badge>
        </div>

        {/* Main progress bar */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-brown-600">Progression globale</span>
            <span className="font-semibold text-mint-600">{progression}%</span>
          </div>
          <ProgressBar value={progression} showLabel={false} size="lg" />
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="text-center p-3 bg-beige-50 rounded-lg">
            <p className="text-2xl font-bold text-brown-900">{milestones.length}</p>
            <p className="text-xs text-brown-500">Jalons</p>
          </div>
          <div className="text-center p-3 bg-mint-50 rounded-lg">
            <p className="text-2xl font-bold text-mint-700">{completedMilestones.length}</p>
            <p className="text-xs text-brown-500">Termines</p>
          </div>
          <div className="text-center p-3 bg-gold-50 rounded-lg">
            <p className="text-2xl font-bold text-gold-700">{pendingMilestones.length}</p>
            <p className="text-xs text-brown-500">En attente</p>
          </div>
        </div>

        {/* Milestones timeline */}
        {sortedMilestones.length > 0 ? (
          <div className="pt-4 border-t border-beige-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-brown-900">Jalons</h4>
              {!readOnly && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Plus className="w-4 h-4" />}
                  onClick={() => setShowAddModal(true)}
                >
                  Ajouter
                </Button>
              )}
            </div>
            <div className="space-y-0">
              {sortedMilestones.map((milestone, index) => (
                <div
                  key={milestone.id}
                  className={index === sortedMilestones.length - 1 ? 'last-milestone' : ''}
                >
                  <MilestoneItem
                    milestone={milestone}
                    onComplete={() => handleCompleteMilestone(milestone.id)}
                    onDelete={() => handleDeleteMilestone(milestone.id)}
                    readOnly={readOnly}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="pt-4 border-t border-beige-200 text-center py-8">
            <Circle className="w-10 h-10 text-beige-300 mx-auto mb-3" />
            <p className="text-brown-500 mb-3">Aucun jalon defini</p>
            {!readOnly && (
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Plus className="w-4 h-4" />}
                onClick={() => setShowAddModal(true)}
              >
                Ajouter un jalon
              </Button>
            )}
          </div>
        )}

        {/* Status progression visual */}
        <div className="pt-4 border-t border-beige-200">
          <h4 className="font-medium text-brown-900 mb-4">Etapes du projet</h4>
          <div className="flex items-center justify-between">
            {Object.entries(PROJECT_STATUS_CONFIG).map(([key, config], index, arr) => {
              const isActive = key === status
              const isPassed =
                Object.keys(PROJECT_STATUS_CONFIG).indexOf(key) <
                Object.keys(PROJECT_STATUS_CONFIG).indexOf(status)
              const isLast = index === arr.length - 1

              return (
                <div key={key} className="flex items-center flex-1">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium ${
                        isPassed || isActive
                          ? 'bg-mint-500 text-white'
                          : 'bg-beige-200 text-brown-400'
                      }`}
                    >
                      {isPassed ? (
                        <Check className="w-4 h-4" />
                      ) : (
                        index + 1
                      )}
                    </div>
                    <span
                      className={`text-xs mt-1.5 text-center max-w-[60px] ${
                        isActive ? 'font-semibold text-brown-900' : 'text-brown-500'
                      }`}
                    >
                      {config.label}
                    </span>
                  </div>
                  {!isLast && (
                    <div
                      className={`flex-1 h-0.5 mx-1 ${
                        isPassed ? 'bg-mint-500' : 'bg-beige-200'
                      }`}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Add milestone modal */}
      <AddMilestoneModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onAdd={handleAddMilestone}
      />

      {/* Custom CSS for last milestone */}
      <style>{`
        .last-milestone > div > .absolute {
          display: none;
        }
      `}</style>
    </Card>
  )
}

export default ProgressTracker
